import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { House } from '@/types/admin';
import { getColocationTenants } from '@/lib/coloc-api/admin';

interface PaymentFormData {
  amount: string;
  paymentType: string;
  houseId: string;
  description: string;
  tenantId: string;
  providerId: string;
}

interface Tenant {
  id: number;
  name: string;
  room_number: string;
}

interface ServiceProvider {
  id: number;
  name: string;
}

export const usePaymentForm = () => {
  const [formData, setFormData] = useState<PaymentFormData>({
    amount: '',
    paymentType: '',
    houseId: '',
    description: '',
    tenantId: '',
    providerId: '',
  });

  const [selectedHouse, setSelectedHouse] = useState<House | undefined>();
  const [selectedTenant, setSelectedTenant] = useState<Tenant | undefined>();
  const [selectedProvider, setSelectedProvider] = useState<ServiceProvider | undefined>();
  const [housesQuery, setHousesQuery] = useState('');

  const { data: tenantsResponse } = useQuery({
    queryKey: ['tenants', selectedHouse?.id],
    queryFn: () => getColocationTenants(selectedHouse!.id),
    enabled: !!selectedHouse,
  });

  const tenants = tenantsResponse?.tenants || [];

  // Update formData when selections change
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      houseId: selectedHouse?.id?.toString() || '',
      tenantId: selectedTenant?.id?.toString() || '',
      providerId: selectedProvider?.id?.toString() || '',
    }));
  }, [selectedHouse, selectedTenant, selectedProvider]);

  // Clear dependent selections when house changes
  useEffect(() => {
    setSelectedTenant(undefined);

    if (selectedHouse === undefined) {
      setFormData((prev) => ({
        ...prev,
        houseId: '',
        tenantId: '',
      }));
    }
  }, [selectedHouse]);

  const resetForm = () => {
    setFormData({
      amount: '',
      paymentType: '',
      houseId: '',
      description: '',
      tenantId: '',
      providerId: '',
    });
    setSelectedHouse(undefined);
    setSelectedTenant(undefined);
    setSelectedProvider(undefined);
    setHousesQuery('');
  };

  const updateFormData = (updates: Partial<PaymentFormData>) => {
    setFormData((prev) => ({
      ...prev,
      ...updates,
    }));
  };

  return {
    formData,
    setFormData: updateFormData,
    selectedHouse,
    setSelectedHouse,
    selectedTenant,
    setSelectedTenant,
    selectedProvider,
    setSelectedProvider,
    housesQuery,
    setHousesQuery,
    tenants,
    resetForm,
  };
}; 